import React, { useState, useEffect } from 'react';

interface FileReceiver {
  id: string;
  slug: string;
  name: string;
  url: string;
  createdAt: string;
  filesCount?: number;
}

export default function FastFilesDashboard() {
  const [receivers, setReceivers] = useState<FileReceiver[]>([]);
  const [email, setEmail] = useState('');
  const [isLoading, setIsLoading] = useState(true);
  const [inputEmail, setInputEmail] = useState('');
  const [isSaving, setIsSaving] = useState(false);
  const [magicLinkSent, setMagicLinkSent] = useState(false);

  useEffect(() => {
    loadReceivers();
    checkForMagicToken();
  }, []);

  const checkForMagicToken = async () => {
    const params = new URLSearchParams(window.location.search);
    const token = params.get('token');

    if (token) {
      try {
        const response = await fetch(`https://happyhumantools-d1-api.b-9f2.workers.dev/api/get-receivers?token=${token}`);
        const data = await response.json();

        if (data.success) {
          setEmail(data.email);
          localStorage.setItem('userEmail', data.email);

          // Merge remote receivers with local ones
          const localReceivers = JSON.parse(localStorage.getItem('myFileReceivers') || '[]');
          const remoteReceivers = data.receivers.map((r: any) => ({
            id: r.id,
            slug: r.slug,
            name: r.name,
            url: `https://happyhumantools.com/files/${r.slug}`,
            createdAt: r.created_at,
            filesCount: r.files_uploaded
          }));

          // Combine and deduplicate
          const allReceivers = [...remoteReceivers, ...localReceivers];
          const uniqueReceivers = allReceivers.filter((r, index, self) =>
            index === self.findIndex(t => t.slug === r.slug)
          );

          setReceivers(uniqueReceivers);
          localStorage.setItem('myFileReceivers', JSON.stringify(uniqueReceivers));

          // Clean URL
          window.history.replaceState({}, '', '/dashboard');
        }
      } catch (error) {
        console.error('Failed to verify token:', error);
      }
    }
  };

  const loadReceivers = async () => {
    // Check if user has email in localStorage (logged in)
    const savedEmail = localStorage.getItem('userEmail');

    if (savedEmail) {
      setEmail(savedEmail);
      // TODO: Fetch receivers from API based on email
      // For now, use localStorage
    }

    // Load anonymous receivers from localStorage
    const localReceivers = JSON.parse(localStorage.getItem('myFileReceivers') || '[]');
    // Update URLs to use the working /files/ path
    const updatedReceivers = localReceivers.map((r: FileReceiver) => ({
      ...r,
      url: r.url.includes('files.happyhumantools.com')
        ? `https://happyhumantools.com/files/${r.slug}`
        : r.url
    }));
    setReceivers(updatedReceivers);
    setIsLoading(false);
  };

  const handleCopyLink = (url: string) => {
    navigator.clipboard.writeText(url);
    // Visual feedback
    const btn = event?.target as HTMLElement;
    if (btn) {
      const originalText = btn.textContent;
      btn.textContent = '✓ Copied!';
      setTimeout(() => {
        btn.textContent = originalText;
      }, 2000);
    }
  };

  const handleDelete = async (receiver: FileReceiver) => {
    if (!confirm(`Are you sure you want to delete "${receiver.name}"? This action cannot be undone.`)) {
      return;
    }

    try {
      const response = await fetch(`https://happyhumantools-d1-api.b-9f2.workers.dev/api/upload-links/${receiver.slug}/delete`, {
        method: 'DELETE',
        headers: { 'Content-Type': 'application/json' }
      });

      const result = await response.json();

      if (result.success) {
        // Remove from state
        setReceivers(receivers.filter(r => r.id !== receiver.id));

        // Update localStorage
        const localReceivers = receivers.filter(r => r.id !== receiver.id);
        localStorage.setItem('myFileReceivers', JSON.stringify(localReceivers));
      } else {
        alert('Failed to delete. Please try again.');
      }
    } catch (error) {
      console.error('Delete error:', error);
      alert('Failed to delete. Please try again.');
    }
  };

  const formatDate = (dateStr: string) => {
    const date = new Date(dateStr);
    const now = new Date();
    const diffTime = Math.abs(now.getTime() - date.getTime());
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

    if (diffDays === 0) return 'Today';
    if (diffDays === 1) return 'Yesterday';
    if (diffDays < 7) return `${diffDays} days ago`;
    return date.toLocaleDateString();
  };

  const handleSaveReceivers = async () => {
    if (!inputEmail) return;

    setIsSaving(true);
    try {
      const response = await fetch('https://happyhumantools-d1-api.b-9f2.workers.dev/api/save-receivers', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          email: inputEmail,
          receivers: receivers
        })
      });

      const result = await response.json();

      if (result.success) {
        setMagicLinkSent(true);
        setEmail(inputEmail);
        localStorage.setItem('userEmail', inputEmail);

        // Show the magic link for testing (remove in production)
        console.log('Magic link:', result.magicLink);

        // Auto-open magic link for testing
        if (result.magicLink) {
          setTimeout(() => {
            window.open(result.magicLink, '_blank');
          }, 1000);
        }
      }
    } catch (error) {
      console.error('Failed to save receivers:', error);
      alert('Failed to save. Please try again.');
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <div className="flex items-center">
              <a href="/" className="text-2xl font-bold text-gray-900">FastFiles</a>
            </div>
            <nav className="flex gap-6">
              <a href="/" className="text-gray-600 hover:text-gray-900">Create New</a>
              {email ? (
                <span className="text-gray-600">{email}</span>
              ) : (
                <a href="/login" className="text-blue-600 hover:text-blue-700 font-medium">Sign In</a>
              )}
            </nav>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-4 py-8">
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900 mb-2">My File Receivers</h1>
          <p className="text-gray-600">Manage your file upload pages and view received files</p>
        </div>

        {isLoading ? (
          <div className="text-center py-12">
            <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-gray-900"></div>
          </div>
        ) : receivers.length === 0 ? (
          <div className="bg-white rounded-lg shadow border border-gray-200 p-12 text-center">
            <div className="text-6xl mb-4">📭</div>
            <h2 className="text-2xl font-semibold text-gray-900 mb-2">No file receivers yet</h2>
            <p className="text-gray-600 mb-6">Create your first file upload page to start receiving files</p>
            <a
              href="/"
              className="inline-block px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 font-medium"
            >
              Create Your First Receiver
            </a>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {receivers.map((receiver) => (
              <div key={receiver.id} className="bg-white rounded-lg shadow border border-gray-200 p-6">
                <div className="flex items-start justify-between mb-4">
                  <div className="text-3xl">📁</div>
                  <span className="text-sm text-gray-500">{formatDate(receiver.createdAt)}</span>
                </div>

                <h3 className="text-xl font-semibold text-gray-900 mb-2">{receiver.name}</h3>

                <div className="text-sm text-gray-600 mb-4">
                  <div className="truncate">{receiver.url}</div>
                  {receiver.filesCount !== undefined && (
                    <div className="mt-1">{receiver.filesCount} files received</div>
                  )}
                </div>

                <div className="flex gap-2">
                  <a
                    href={`/receiver/${receiver.slug}`}
                    className="flex-1 px-3 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 text-center font-medium text-sm"
                  >
                    View Files
                  </a>
                  <button
                    onClick={() => handleCopyLink(receiver.url)}
                    className="flex-1 px-3 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 font-medium text-sm"
                  >
                    Copy Link
                  </button>
                </div>
                <button
                  onClick={() => handleDelete(receiver)}
                  className="mt-3 w-full px-3 py-2 bg-red-50 text-red-600 rounded-lg hover:bg-red-100 font-medium text-sm border border-red-200"
                >
                  Delete
                </button>
              </div>
            ))}
          </div>
        )}

        {!email && receivers.length > 0 && (
          <div className="mt-12 bg-blue-50 rounded-lg p-6 border border-blue-200">
            <h3 className="text-lg font-semibold text-blue-900 mb-2">
              💡 Save your file receivers
            </h3>
            <p className="text-blue-800 mb-4">
              Enter your email to save these file receivers and access them from any device
            </p>
            <div className="flex gap-3">
              <input
                type="email"
                value={inputEmail}
                onChange={(e) => setInputEmail(e.target.value)}
                placeholder="your@email.com"
                className="flex-1 px-4 py-2 border border-blue-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                disabled={isSaving}
              />
              <button
                onClick={handleSaveReceivers}
                disabled={!inputEmail || isSaving}
                className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 font-medium disabled:opacity-50"
              >
                {isSaving ? 'Saving...' : 'Save & Get Access Link'}
              </button>
            </div>
            {magicLinkSent && (
              <div className="mt-3 text-green-700 bg-green-50 p-3 rounded-lg">
                📧 Check your email! We've sent you a magic link to access your dashboard.
              </div>
            )}
          </div>
        )}
      </main>
    </div>
  );
}